import { Colors } from "@/enums/Colors";
import { Debouncer } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/Debouncer";
import { useResponsive } from "@/hooks/useResponsive";
import { ArrowDownward, ArrowUpward, UnfoldMore } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  Skeleton,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Theme,
  checkboxClasses,
  styled,
  svgIconClasses,
  tableCellClasses,
  typographyClasses,
  useTheme,
} from "@mui/material";
import { ChangeEvent, Dispatch, SetStateAction, createRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CommonTableMobileCard, MobileCardConfig } from "./CommonTableMobileCard";
import { CommonTableMobileHeader } from "./CommonTableMobileHeader";

const StyledTableHeader = styled(TableRow)({
  backgroundColor: "white",
  borderTop: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  [`& .${tableCellClasses.root}`]: {
    minWidth: "fit-content",
    width: "25%",
    textWrap: "nowrap",
    whiteSpace: "nowrap",
    height: "2.750rem",
    fontSize: "0.750rem",
    fontWeight: "600",
    color: `${Colors.LIGHT_GRAY_8A8E93}`,
  },
});

const stickyColumn = (leftColumn: boolean, theme: Theme, width?: string) => ({
  minWidth: leftColumn ? width || "17.062rem" : "fit-content",
  maxWidth: leftColumn && width ? width : undefined,
  width: leftColumn && width ? width : undefined,
  position: "sticky",
  backgroundColor: "inherit",
  zIndex: theme.zIndex.appBar + 1,
  ...(leftColumn ? { left: 0 } : { right: 0 }),
});

const RightBoxShadowContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "1rem",
  width: "100%",
  height: "100%",
  "::after": {
    content: '""',
    position: "absolute",
    right: 0,
    display: "inline-flex",
    height: "100%",
    marginLeft: "auto",
    width: "1px",
    boxShadow: "0.250rem 0 0.688rem rgba(0, 0, 0, 0.34)",
  },
});

const StyledCheckbox = styled(Checkbox)({
  backgroundColor: "#fff",
  width: "1.000rem",
  height: "1.000rem",
  color: `${Colors.LIGHT_GRAY_DEDEE1}`,
  [`& .${svgIconClasses.root}`]: {
    fontSize: "1.5rem",
  },
  [`&.${checkboxClasses.checked}`]: {
    color: `${Colors.BUTTON_PURPLE}`,
  },
  [`&.${checkboxClasses.disabled}`]: {
    color: `${Colors.ALMOST_WHITE}`,
  },
});

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== "rowColor",
})<{ rowColor?: string }>(({ rowColor }) => ({
  border: 0,
  borderTop: `0.062rem solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  borderBottom: `0.062rem solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  backgroundColor: rowColor ? `${rowColor}` : "#fff",
  ":hover": {
    backgroundColor: !rowColor && `${Colors.BUTTON_SECONDARY_PURPLE}`,
    "& .btn-ai": {
      // Enables the display of the AI button
      display: "flex !important",
    },
  },
  [`& .${tableCellClasses.root}`]: {
    border: 0,
    maxWidth: "15.875rem",
    padding: "0 0.500rem",
    overflow: "clip",
    overflowClipMargin: "0.688rem",
    whiteSpace: "nowrap",
    height: "3.000rem",
    borderLeft: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
    ":is(:first-of-type)": {
      paddingRight: "0",
    },
    "&.disable-right-border": {
      borderRight: `0rem`,
    },
    ":hover": {
      overflowX: "auto",
    },
    [`& .${typographyClasses.root}`]: {
      fontSize: "0.750rem",
      fontWeight: "400",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
    },
  },
}));

const ActionsTableRow = styled(TableRow)(({ theme }) => ({
  minWidth: "fit-content",
  position: "sticky",
  backgroundColor: "inherit",
  zIndex: theme.zIndex.appBar,
  right: 0,
  display: "flex",
  height: "3rem",
  justifyContent: "space-evenly",
  alignItems: "center",
  background: "inherit",
  boxShadow: "-0.500rem 0 0.500rem -0.625rem rgba(0, 0, 0, 0.14)",
  borderLeft: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  [`& .${tableCellClasses.root}`]: {
    ":first-of-type": {
      padding: "0 0.4rem",
    },
    padding: "0 0.4rem",
    display: "flex",
    alignItems: "center",
    flex: "1",
    justifyContent: "center",
    borderRight: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  },
}));

const NameContainer = styled(Box)({
  display: "flex",
  gap: "0.500rem",
  alignItems: "center",
  maxWidth: "99%",
  overflowX: "auto",
});

const CellLabelContainer = styled(Box)<{
  sticky?: boolean;
  nextIsSticky?: boolean;
}>(({ sticky }) => ({
  borderRight: 0,
  width: "100%",
  ...(sticky && {
    height: "100%",
    display: "grid",
    placeItems: "center",
    backgroundColor: "white",
    textAlign: "center",
    borderLeft: `0.062rem solid ${Colors.LIGHT_GRAY_DEDEE1}`,
    borderRight: `0.062rem solid ${Colors.LIGHT_GRAY_DEDEE1}`,
    "& svg": {
      display: "none",
    },
  }),
}));

const StickyRightTableCell = styled(TableCell)<{ firststicky?: boolean }>(({ firststicky }) => ({
  padding: "0rem !important",
  position: "sticky",
  right: 0,
  backgroundColor: "inherit",
  borderLeft: "0px !important",
  borderRight: "0px !important",
  ...(firststicky && {
    "::before": {
      content: '""',
      position: "absolute",
      left: 0,
      display: "inline-flex",
      height: "100%",
      marginRight: "auto",
      width: "1px",
      boxShadow: "-0.250rem 0 0.688rem rgba(0, 0, 0, 0.34)",
    },
  }),
}));

const StickyRowCellContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  height: "100%",
  "& .custom-border-divider": {
    height: "inherit",
    width: "1px !important",
    borderLeft: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  },
  "& .sticky-content-container": {
    width: "100%",
    display: "grid",
    placeItems: "center",
  },
});

const StickyHeaderCellContainer = styled(Box)<{ smartlistVariant?: boolean }>(({ smartlistVariant }) => ({
  borderTop: smartlistVariant && `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  display: "flex",
  alignItems: "center",
  columnGap: "10px",
  "& .custom-border-divider": {
    width: "1px",
    height: smartlistVariant ? "2.75rem" : "24px",
    borderLeft: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  },
}));

interface SortableHeaderLabelProps {
  label: string;
  isActive: boolean;
  direction: "asc" | "desc";
  onClick: () => void;
  sx?: SxProps;
}

const SORTABLE_LABEL_BASE_SX = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.25rem",
  cursor: "pointer",
  userSelect: "none",
};

const SORT_ICON_INACTIVE_SX = {
  display: "inline-flex",
  alignItems: "center",
  color: Colors.LIGHT_GRAY_8A8E93,
};

function SortableHeaderLabel({ label, isActive, direction, onClick, sx }: SortableHeaderLabelProps) {
  return (
    <Box onClick={onClick} sx={{ ...SORTABLE_LABEL_BASE_SX, ...((sx as object) || {}) }}>
      {label}
      {isActive ? (
        direction === "asc" ? (
          <ArrowUpward fontSize="inherit" />
        ) : (
          <ArrowDownward fontSize="inherit" />
        )
      ) : (
        <Box sx={SORT_ICON_INACTIVE_SX}>
          <UnfoldMore fontSize="inherit" />
        </Box>
      )}
    </Box>
  );
}

export const PARENT_NODE: DataNode = {
  checked: false,
  externalId: "Top Node",
  children: [],
  cells: [],
};

export type HeaderCells = {
  id: string;
  label: string;
  /** Whether the column should be sticky to the right border */
  sticky?: boolean;
  /** Optional. Used to calculate the space the first sticky column should shift from the other sticky columns */
  width?: string;
  hideOrderBy?: boolean;
  sx?: SxProps;
};

export type DataNode = {
  externalId: string;
  color?: string;
  cells: JSX.Element[];
  children: DataNode[];
  checked: boolean;
  entity?: any;
};

export type CommonTableProps = {
  loading?: boolean;
  headerCells: HeaderCells[];
  setData: Dispatch<SetStateAction<DataNode>>;
  data: DataNode;
  actions: JSX.Element[]; // Rightmost element on table
  // TODO: How to make header's specific to the right side???
  actionsHeaderCells?: HeaderCells[];
  headerSxProps?: SxProps<Theme>;
  rowsSxProps?: SxProps<Theme>;
  hideCheckboxes?: boolean;
  customerDetailsUsers?: boolean;
  setSelectedItems?: (selectedItems: DataNode[]) => void;
  clearChecks?: boolean;
  setHoveredRowId?: (rowId: string | null) => void;
  hoveredRowId?: string | null;
  setClearChecks?: (value: boolean) => void;
  onClickedRow?: (node: DataNode) => void;
  clickedRowId?: string | null;
  isDeleting?: boolean;
  onSorting?: (order: "asc" | "desc", orderBy: string) => void;
  updateFlag?: boolean;
  smartListVariant?: boolean;
  setPageNumber?: () => void;
  loadingData?: boolean;
  containerSx?: SxProps;
  defaultOrderBy?: string;
  defaultOrder?: "asc" | "desc";
  resizeColumns?: boolean;
  resizedTableMinWidth?: string;
  loadingPlaceholderRowsCount?: number;
  onCheckboxClick?: (node: DataNode, checked: boolean) => void;

  // Responsive props - opt-out by setting disableResponsive to true
  disableResponsive?: boolean;
  mobileCardConfig?: MobileCardConfig;
  /** When true, always shows sort icons: UnfoldMore (⇅) for inactive columns, directional arrow for active */
  alwaysShowSortIcon?: boolean;
};

const debouncer = new Debouncer(100);

function CommonTable(props: CommonTableProps) {
  const columnRefs = useRef([]);
  const isResizing = useRef(-1);
  const DEFAULT_MIN_WIDTH_CELL = 100;
  const DEFAULT_MAX_WIDTH_CELL = 600;

  const theme = useTheme();
  const { isMobile, isTablet } = useResponsive();
  const [order, setOrder] = useState<"asc" | "desc">(props.defaultOrder || "desc");
  // Who tf set this to such default ???
  const [orderBy, setOrderBy] = useState<string>(props.defaultOrderBy || "dueDate");
  const [selectedItems, setSelectedItems] = useState<DataNode[]>([]);

  const fetchOnScrollObserver = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    columnRefs.current = props.headerCells.slice(1).map(() => createRef());
    document.onmousemove = handleOnMouseMove;
    document.onmouseup = handleOnMouseUp;
    return () => {
      document.onmousemove = null;
      document.onmouseup = null;
    };
  }, []);

  const adjustWidthColumn = (index: number, width: number) => {
    const minWidth = DEFAULT_MIN_WIDTH_CELL;
    const maxWidth = DEFAULT_MAX_WIDTH_CELL;
    const newWidth = width > maxWidth ? maxWidth : width < minWidth ? minWidth : width;

    const refElement = columnRefs.current[index]?.current;
    if (refElement && refElement.parentElement) {
      refElement.parentElement.style.width = newWidth + "px";
    }
  };

  function setCursorDocument(isResizing: boolean) {
    document.body.style.cursor = isResizing ? "col-resize" : "auto";
  }

  function handleOnMouseMove(e: MouseEvent) {
    if (isResizing.current >= 0) {
      const newWidth = e.clientX - columnRefs.current[isResizing.current]?.current?.parentElement?.getBoundingClientRect().left;
      adjustWidthColumn(isResizing.current, newWidth);
    }
  }

  function handleOnMouseUp() {
    isResizing.current = -1;
    setCursorDocument(false);
  }

  function onClickResizeColumn(index: number) {
    isResizing.current = index;
    setCursorDocument(true);
  }

  const lastRowRef = useCallback(
    (message: any) => {
      if (!props.setPageNumber) return;
      if (props.loadingData) return;
      if (fetchOnScrollObserver.current) fetchOnScrollObserver.current.disconnect();
      fetchOnScrollObserver.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          props.setPageNumber();
        }
      });
      if (message) fetchOnScrollObserver.current.observe(message);
    },
    [props.loadingData]
  );

  useEffect(() => {
    if (props.updateFlag !== null && props.onSorting) {
      debouncer.executeLastCall(() => {
        props.onSorting(order, orderBy);
      });
    }
  }, [order, orderBy, props.updateFlag]);

  useEffect(() => {
    if (props.setSelectedItems) {
      props.setSelectedItems(selectedItems);
    }
  }, [selectedItems]);

  function handleCheckboxChange(node: DataNode, checked: boolean) {
    setSelectedItems((prevSelectedItems) => {
      if (checked) {
        return [...prevSelectedItems, node];
      } else {
        return prevSelectedItems.filter((item) => item.externalId !== node.externalId);
      }
    });
  }

  useEffect(() => {
    if (props.clearChecks) {
      const cloneNode = (node: DataNode): DataNode => {
        return {
          ...node,
          children: node.children ? node.children.map(cloneNode) : [],
        };
      };
      const dataClone = cloneNode(props.data);
      checkChildren(dataClone, false);
      props.setData(dataClone);
      setSelectedItems([]);
      props.setClearChecks(false);
    }
  }, [props.clearChecks]);

  function handleAllCheckboxClick(event: ChangeEvent<HTMLInputElement>, value: boolean) {
    const checked = event.target.checked;
    const cloneNode = (node: DataNode): DataNode => {
      return {
        ...node,
        children: node.children ? node.children.map(cloneNode) : [],
      };
    };
    const dataClone = cloneNode(props.data);
    checkChildren(dataClone, value);
    props.setData(dataClone);
    if (checked) {
      setSelectedItems(dataClone.children);
    } else {
      setSelectedItems([]);
    }
  }

  function handleOrderBy(id: string) {
    let newOrder: "asc" | "desc" = "desc";
    if (id === orderBy) {
      newOrder = order === "asc" ? "desc" : "asc";
    }
    setOrder(newOrder);
    setOrderBy(id);
  }

  function findNode(node: DataNode, nodeExternalId: string): DataNode | null {
    if (node.externalId === nodeExternalId) return node;
    if (!node.children || node.children.length === 0) return null;
    let found = null;
    for (let i = 0; i < node.children.length; i++) {
      if (found) break;
      found = findNode(node.children[i], nodeExternalId);
    }
    return found;
  }

  function checkChildren(node: DataNode, value: boolean) {
    node.checked = value;
    if (node.children && node.children.length > 0) {
      for (let i = 0; i < node.children.length; i++) {
        checkChildren(node.children[i], value);
      }
    }
  }

  function cloneNode(node: DataNode): DataNode {
    return {
      ...node,
      children: node.children ? node.children.map(cloneNode) : [],
    };
  }

  function handleCellCheckboxClick(node: DataNode, e: React.MouseEvent): void {
    const checked: boolean = (e.target as any).checked;

    props.onCheckboxClick?.(node, checked);

    handleCheckboxChange(node, checked);
    const dataClone = cloneNode(props.data);
    const n = findNode(dataClone, node.externalId);
    if (n) {
      checkChildren(n, checked);
    }
    props.setData(dataClone);
  }

  function handleUserRowHover(rowId: string) {
    if (props.setHoveredRowId) {
      props.setHoveredRowId(rowId);
    }
  }

  function walkTree(tree: DataNode): React.ReactNode {
    return tree.children.map((node, nIdx) => (
      <>
        <StyledTableRow
          ref={props.setPageNumber && tree.children.length - 1 === nIdx ? lastRowRef : null}
          key={node.externalId!}
          rowColor={node.color}
          sx={props.rowsSxProps}
        >
          {/* Sticky Left Cell */}
          <TableCell
            sx={{
              ...stickyColumn(true, theme, props.headerCells[0]?.width),
              borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1} !important`,
              borderLeft: `1px solid ${Colors.LIGHT_GRAY_DEDEE1} !important`,
              borderRight: `1px solid ${Colors.LIGHT_GRAY_DEDEE1} !important`,
            }}
          >
            <RightBoxShadowContainer>
              {!props.hideCheckboxes && (
                <StyledCheckbox
                  disabled={props.isDeleting}
                  checked={node.checked}
                  indeterminate={node.children.some((n) => n.checked) && !node.children.every((n) => n.checked)}
                  onClick={(e) => handleCellCheckboxClick(node, e)}
                />
              )}
              <NameContainer>{node.cells[0]}</NameContainer>
            </RightBoxShadowContainer>
          </TableCell>

          {/* Normal cells */}
          {node.cells.map((cell, idx) => {
            return !props.headerCells[idx]?.sticky && idx !== 0 ? (
              <TableCell
                onClick={() => props.onClickedRow?.(node)}
                onMouseEnter={() => handleUserRowHover(node.externalId!)}
                //onMouseLeave={handleUserRowNotHover}
                key={idx + nIdx * 1000}
                sx={{
                  borderRight: props.headerCells[idx + 1]?.sticky ? "0px !important" : "",
                  borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1} !important`,
                  cursor: props.onClickedRow ? "pointer" : "default",
                  ":last-of-type": {
                    borderRight: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
                  },
                  ...(props.headerCells[idx]?.width && {
                    width: props.headerCells[idx].width,
                    minWidth: props.headerCells[idx].width,
                  }),
                }}
              >
                {cell}
              </TableCell>
            ) : (
              <></>
            );
          })}

          {/* Sticky Right Cells */}
          {node.cells.map((cell, idx) => {
            return (
              props.headerCells[idx]?.sticky && (
                <StickyRightTableCell
                  sx={{
                    width: props.headerCells[idx]?.width ? `${props.headerCells[idx]?.width} !important` : "auto",
                    right: props.headerCells[idx + 1]?.sticky ? `calc(${props.headerCells[idx + 1]?.width} + 10px)` : "0",
                    borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1} !important`,
                  }}
                  firststicky={!props.headerCells[idx - 1]?.sticky}
                  key={idx + nIdx * 1000}
                  onMouseEnter={() => handleUserRowHover(node.externalId!)}
                >
                  <StickyRowCellContainer
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <Box className="custom-border-divider" />
                    <Box className="sticky-content-container">{cell}</Box>
                  </StickyRowCellContainer>
                </StickyRightTableCell>
              )
            );
          })}

          {/* Actions*/}
          {props.actions && props.actions.length > 0 && (
            <ActionsTableRow>
              {props.actions.map((action, idx) => (
                <TableCell key={idx + nIdx * 10000}>{action}</TableCell>
              ))}
            </ActionsTableRow>
          )}
        </StyledTableRow>
        {walkTree(node)}
      </>
    ));
  }

  const selectedCount = props.data.children.reduce((acc, node) => {
    if (node.checked) {
      return acc + 1;
    } else {
      return acc;
    }
  }, 0);

  const childrenLength = props.data.children.length;

  const StyledTableContainer = useMemo(() => {
    return props.customerDetailsUsers
      ? styled(TableContainer)`
          transform: rotateX(180deg);
        `
      : TableContainer;
  }, [props.customerDetailsUsers]);

  const StyledTable = useMemo(() => {
    return props.customerDetailsUsers
      ? styled(Table)`
          transform: rotateX(180deg);
          border-collapse: separate;
          border-spacing: 0;
        `
      : styled(Table)(() => ({
          minWidth: props.resizedTableMinWidth ?? "none",
          "table-layout": props.resizeColumns ? "fixed" : "auto",
          borderCollapse: "separate",
          borderSpacing: 0,
          maxWidth: "100%",
          ".resizeLine": {
            position: "absolute",
            height: "100%",
            width: "4px",
            top: 0,
            right: "-2px",
            cursor: "col-resize",
            "&:hover": {
              "background-color": Colors.LAZO_PURPLE,
            },
            "&:active": {
              "background-color": Colors.LAZO_PURPLE,
            },
          },
        }));
  }, [props.customerDetailsUsers, props.resizedTableMinWidth, props.resizeColumns]);

  // Check if table has sticky columns (which might need special handling)
  const hasStickyColumns = props.headerCells.some((cell) => cell.sticky);

  // Find non-sticky columns for priority display in mobile
  const nonStickyIndices = props.headerCells.map((cell, idx) => (!cell.sticky ? idx : null)).filter((idx) => idx !== null && idx !== 0);

  // Smart defaults for mobile card configuration
  const defaultMobileCardConfig: MobileCardConfig = {
    primaryFieldIndex: 0,
    showExpandButton: true,
    expandedByDefault: false,
    collapsedFieldsCount: Math.min(2, nonStickyIndices.length),
    hideSecondaryFields: false,
    priorityFields: nonStickyIndices.slice(0, 2),
    // Show first 2 non-sticky fields
    alwaysShowActions: true,
  };

  const mobileCardConfig = {
    ...defaultMobileCardConfig,
    ...props.mobileCardConfig,
  };

  // Determine if we should show mobile view (opt-out approach)
  // Auto-disable responsive for complex tables with many sticky columns unless explicitly enabled
  const autoDisableResponsive = hasStickyColumns && props.headerCells.filter((c) => c.sticky).length > 2;
  const showMobileView =
    !props.disableResponsive && !autoDisableResponsive && (isMobile || (isTablet && mobileCardConfig.showExpandButton));

  const handleSortSelection = (columnId: string, newOrder?: "asc" | "desc") => {
    if (columnId === orderBy && !newOrder) {
      // Toggle order if same column
      const toggledOrder = order === "asc" ? "desc" : "asc";
      setOrder(toggledOrder);
    } else if (newOrder) {
      // Set specific order
      setOrder(newOrder);
      setOrderBy(columnId);
    } else {
      // New column, default to desc
      setOrder("desc");
      setOrderBy(columnId);
    }
  };

  if (showMobileView) {
    return (
      <Box sx={{ padding: "0.75rem", ...props.containerSx }}>
        {/* Mobile Header with sorting */}
        <CommonTableMobileHeader
          hideCheckboxes={props.hideCheckboxes}
          selectedCount={selectedCount}
          childrenLength={childrenLength}
          onAllCheckboxClick={handleAllCheckboxClick}
          onSortSelection={handleSortSelection}
          headerCells={props.headerCells}
          order={order}
          orderBy={orderBy}
        />

        {/* Mobile Cards */}
        {!props.loading && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "0.750rem" }}>
            {props.data.children.map((node, idx) => (
              <Box key={node.externalId} ref={props.setPageNumber && props.data.children.length - 1 === idx ? lastRowRef : null}>
                <CommonTableMobileCard
                  node={node}
                  headerCells={props.headerCells}
                  onCheckboxClick={(node, checked) => {
                    handleCheckboxChange(node, checked);
                    props.onCheckboxClick?.(node, checked);
                    const dataClone = cloneNode(props.data);
                    const n = findNode(dataClone, node.externalId);
                    if (n) {
                      checkChildren(n, checked);
                    }
                    props.setData(dataClone);
                  }}
                  onClickedRow={props.onClickedRow}
                  actions={props.actions}
                  isDeleting={props.isDeleting}
                  hideCheckboxes={props.hideCheckboxes}
                  mobileCardConfig={mobileCardConfig}
                />
              </Box>
            ))}
          </Box>
        )}

        {/* Loading state for mobile */}
        {props.loading && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "0.750rem" }}>
            {new Array(props.loadingPlaceholderRowsCount || 10).fill(0).map((_, idx) => (
              <Skeleton
                key={`loading-mobile-${idx}`}
                variant="rectangular"
                height={120}
                sx={{
                  borderRadius: "0.75rem",
                  backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  }

  // Desktop view (existing implementation)
  return (
    <>
      <StyledTableContainer sx={props.containerSx}>
        <StyledTable>
          <TableHead
            sx={{
              position: "sticky",
              top: 0,
              zIndex: theme.zIndex.appBar + 2,
            }}
          >
            <StyledTableHeader sx={props.headerSxProps}>
              {/* First Cell */}
              <TableCell
                sortDirection={orderBy === props.headerCells[0].id ? order : "desc"}
                sx={{
                  ...stickyColumn(true, theme, props.headerCells[0]?.width),
                  padding: "0 0 0 0.5rem",
                  backgroundColor: "white",
                  borderTop: props.smartListVariant ? `1px solid ${Colors.LIGHT_GRAY_DEDEE1}` : `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
                  borderLeft: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
                  borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
                  borderRight: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
                }}
              >
                <RightBoxShadowContainer sx={props.headerCells[0].sx}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.750rem",
                      width: "100%",
                    }}
                  >
                    {!props.hideCheckboxes && (
                      <StyledCheckbox checked={selectedCount == childrenLength && childrenLength !== 0} onChange={handleAllCheckboxClick} />
                    )}
                    {props.headerCells[0].hideOrderBy ? (
                      <Box sx={{ paddingRight: "0.75rem" }}>{props.headerCells[0].label}</Box>
                    ) : props.alwaysShowSortIcon ? (
                      <SortableHeaderLabel
                        label={props.headerCells[0].label}
                        isActive={orderBy === props.headerCells[0].id}
                        direction={order}
                        onClick={() => handleOrderBy(props.headerCells[0].id)}
                      />
                    ) : (
                      <TableSortLabel
                        active={orderBy === props.headerCells[0].id}
                        direction={orderBy === props.headerCells[0].id ? order : "desc"}
                        onClick={() => handleOrderBy(props.headerCells[0].id)}
                      >
                        {props.headerCells[0].label}
                      </TableSortLabel>
                    )}
                  </Box>
                </RightBoxShadowContainer>
              </TableCell>

              {props.headerCells.slice(1).map((cell, idx) => {
                return (
                  !cell.sticky && (
                    <TableCell
                      key={cell.id}
                      sortDirection={orderBy === cell.id ? order : "desc"}
                      sx={{
                        borderLeft: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
                        padding: "0 0 0 0.750rem",
                        backgroundColor: "white",
                        borderTop: props.smartListVariant
                          ? `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`
                          : `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
                        borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
                        ":last-of-type": {
                          borderRight: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
                        },
                      }}
                      style={{ ...(cell.width && { width: cell.width, minWidth: cell.width }) }}
                    >
                      <CellLabelContainer sx={cell.sx} nextIsSticky={props.headerCells.slice(1)[idx + 1]?.sticky}>
                        {cell.hideOrderBy ? (
                          <Box sx={{ paddingRight: "0.75rem" }}>{cell.label}</Box>
                        ) : props.alwaysShowSortIcon ? (
                          <SortableHeaderLabel
                            label={cell.label}
                            isActive={orderBy === cell.id}
                            direction={order}
                            onClick={() => handleOrderBy(cell.id)}
                          />
                        ) : (
                          <TableSortLabel
                            active={orderBy === cell.id}
                            direction={orderBy === cell.id ? order : "desc"}
                            onClick={() => handleOrderBy(cell.id)}
                          >
                            {cell.label}
                          </TableSortLabel>
                        )}
                      </CellLabelContainer>
                      {props.resizeColumns && (
                        <div
                          onMouseDown={() => onClickResizeColumn(idx + 1)}
                          ref={(el) => {
                            if (el) columnRefs.current[idx + 1] = { current: el };
                          }}
                          className={"resizeLine"}
                        />
                      )}
                    </TableCell>
                  )
                );
              })}

              {/* Sticky Right Headers */}
              {props.headerCells.slice(1).map((cell, idx) => {
                return (
                  cell.sticky && (
                    <StickyRightTableCell
                      style={{
                        width: cell?.width ? `${cell.width} !important` : "auto",
                        right: props.headerCells[idx + 2]?.sticky ? `calc(${props.headerCells[idx + 2]?.width} + 10px)` : "0",
                      }}
                      firststicky={!props.headerCells[idx]?.sticky}
                      key={cell.id}
                    >
                      <StickyHeaderCellContainer smartlistVariant={props.smartListVariant}>
                        <Box className="custom-border-divider" />
                        <CellLabelContainer
                          style={{
                            border: "0px !important",
                            width: cell?.width ? `${cell.width} !important` : "auto",
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          {cell.hideOrderBy ? (
                            <Box
                              className="sticky-right-header"
                              sx={{
                                whiteSpace: "break-spaces",
                                paddingRight: "1rem",
                                paddingLeft: "0.375rem",
                              }}
                            >
                              {cell.label}
                            </Box>
                          ) : props.alwaysShowSortIcon ? (
                            <SortableHeaderLabel
                              label={cell.label}
                              isActive={orderBy === cell.id}
                              direction={order}
                              onClick={() => handleOrderBy(cell.id)}
                              sx={{ whiteSpace: "break-spaces", padding: "0.25rem" }}
                            />
                          ) : (
                            <TableSortLabel
                              active={orderBy === cell.id}
                              direction={orderBy === cell.id ? order : "desc"}
                              onClick={() => handleOrderBy(cell.id)}
                              sx={{
                                whiteSpace: "break-spaces",
                                padding: "0.25rem",
                              }}
                            >
                              {cell.label}
                            </TableSortLabel>
                          )}
                        </CellLabelContainer>
                      </StickyHeaderCellContainer>
                    </StickyRightTableCell>
                  )
                );
              })}
            </StyledTableHeader>
          </TableHead>
          {!props.loading && <TableBody>{walkTree(props.data)}</TableBody>}
          {props.loading &&
            new Array(props.loadingPlaceholderRowsCount || 20).fill(0).map((_, idx) => (
              <StyledTableRow key={`loading-${idx}`} sx={props.rowsSxProps}>
                {props.headerCells.map((hCell, idx2) => (
                  <TableCell
                    key={`loading-${idx}-${idx2}`}
                    sx={{
                      ...(idx2 === 0 && stickyColumn(true, theme, props.headerCells[0]?.width)),
                      ...(idx2 !== 0 && hCell.width && { width: hCell.width, minWidth: hCell.width }),
                    }}
                  >
                    <Skeleton
                      variant="rectangular"
                      sx={{
                        width: "70%",
                        height: "1rem",
                        borderRadius: "0.25rem",
                      }}
                    />
                  </TableCell>
                ))}
              </StyledTableRow>
            ))}
        </StyledTable>
      </StyledTableContainer>
    </>
  );
}

export default CommonTable;
